import { replyQuiet } from './rebook-message'
import { inStoreApp } from './store-app'
import { api } from './api'
import { goToNoDate, noDateYet, notSure, type RoundCustomer } from './attention'
import { askAgainLater } from './followup'
import { openBooking } from './booking'
import { byWords, SENT_WORDS, waitingFiles, type ImportRequest } from './import-requests'
import { getMe, worksAlone } from './me'
import { TRADE_OPTIONS } from './trade'
import { el, shortDate, todayISO } from './ui'
import { firstJobWaiting, openFirstJob } from './first-job'
import { calendarIcon } from './book-icons'

// The diary's welcome for a new round (Liam, 2026-09-25: someone new "lands on
// an empty diary with no welcome and no first step"). Four steps, each ticked
// from what the round already holds, so the card finishes itself:
//   1. customers on the round;
//   2. someone whose name goes on the jobs (the owner's own name, which a
//      sign-up is given, or a worker login);
//   3. an answer for every customer with no date (29 Sep);
//   4. a visit booked or done, the first one walked through (29 Sep).
// It sits at the top of the diary until all four are done or the owner hides
// it. Business name, bank details and prices stay in Help's longer "Getting
// started" list. Any read failing shows nothing: a half-known card would tick
// the wrong things.

interface WelcomeStep {
  text: string
  done: boolean
  doneText?: string
  // A line under the step while it is not done ("7 still need a date").
  note?: string
  action?: HTMLElement
}

const hiddenKey = (businessId: number | null | undefined): string => `roundly:welcome-hidden:${businessId ?? 'none'}`
// Hidden during this visit, whether or not the browser let us keep it.
const hiddenNow = new Set<number | null | undefined>()

function isHidden(businessId: number | null | undefined): boolean {
  if (hiddenNow.has(businessId)) return true
  try {
    return localStorage.getItem(hiddenKey(businessId)) === '1'
  } catch {
    return false
  }
}

type WelcomeCustomer = Pick<RoundCustomer, 'id' | 'next_booked' | 'next_booked_source' | 'last_clean' | 'status' | 'overdue_bookings' | 'rebook_messages' | 'follow_up_on' | 'to_call' | 'cycle_days' | 'cycle_text' | 'message_reply' | 'message_reply_on' | 'message_reply_dates' | 'message_reply_job_id' | 'message_replies' | 'reply_states'>

// Four steps (Liam, 29 Sep): customers; someone to do the jobs; an answer for every customer who came in with
// no date (a week, "Not sure: ask them", "Ask again later", "Not interested" or "They'll ask when they want
// one": not every customer has a date, so an answer is what counts); then the first booked visit, walked
// through. A round that has recorded a clean is going, so step 3 is not asked of it.
export function welcomeSteps(customers: readonly WelcomeCustomer[], ownName: string | null, workers: number, established = true, signedUpOn: string | null = null): { customers: boolean; people: boolean; answers: boolean; pending: number; toAsk: number; visit: boolean } {
  const noDate = customers.filter((c) => noDateYet(c as RoundCustomer))
  const toAsk = noDate.filter((c) => notSure(c) && !replyQuiet(c, todayISO())).length
  // Asked already, or waiting to be asked again on a date the owner chose: both are answers.
  const pending = noDate.filter((c) => !notSure(c) && !((c.rebook_messages ?? 0) > 0) && !askAgainLater(c) && !replyQuiet(c, todayISO())).length
  // A round already running (not on its free trial) is not asked for these again. A new round is, even when its
  // import brought in last visits: those are the sheet's history, not work done in Roundly, so they neither answer
  // for the customers with no date nor count as its first booked visit (Astra, 29 Sep: the welcome claimed both).
  // Real work done in Roundly counts on a trial round too: a clean on or after the day the round signed up (the free
  // months count from sign-up). Imported last visits are older than that (Codex, 29 Sep).
  const worked = established ? customers.some((c) => !!c.last_clean) : !!signedUpOn && customers.some((c) => !!c.last_clean && c.last_clean >= signedUpOn)
  return {
    customers: customers.length > 0,
    people: !!ownName?.trim() || workers > 0,
    answers: customers.length > 0 && (worked || pending === 0),
    pending: worked ? 0 : pending,
    toAsk,
    // A visit on a real day: "In 2 weeks" puts a customer in a week with no day, which is an answer (step 3),
    // not yet the first booked visit this step walks through.
    visit: worked || customers.some((c) => !!c.next_booked && c.next_booked_source !== 'week'),
  }
}

// A round that has finished the three steps never goes back to needing them,
// so the diary stops asking once it knows (it redraws on every filter change).
let finishedFor: number | null | undefined
// The last card drawn, put straight back into each new diary drawing while the
// fresh one is worked out, so the diary never jumps (review 2026-09-25).
let lastCard: { businessId: number | null | undefined; card: HTMLElement } | null = null
// The worker count, kept once known for this business: it only matters until
// the owner has a name or a worker, and neither is taken away mid-visit.
let workersKnown: { businessId: number | null | undefined; count: number } | null = null

// `customers` is the diary's own list when it has one, so the welcome does not
// ask for the whole round a second time.
// Resolves true when a welcome card is on show, false when there is none (finished, hidden, not an owner, or a
// read failed): the diary then offers "What's next" in its place (whats-next.ts).
export async function renderWelcome(slot: HTMLElement, customersFromDiary: readonly WelcomeCustomer[] | null = null): Promise<boolean> {
  const me = getMe()
  if (me?.role !== 'owner' || isHidden(me.businessId) || finishedFor === me.businessId) return false
  if (lastCard && lastCard.businessId === me.businessId) slot.replaceChildren(lastCard.card)
  let customers: readonly WelcomeCustomer[]
  let workers: number
  // A customer file sent to Roundly and still waiting (0039): the customers step says when they come.
  // Never fails the card: a failed read is no file.
  let waiting: ImportRequest[] = []
  try {
    const [c, w, sent] = await Promise.all([
      customersFromDiary ?? api<{ customers: RoundCustomer[] }>('/api/customers', 'customers').then((r) => r.customers),
      workersKnown && workersKnown.businessId === me.businessId
        ? workersKnown.count
        : api<{ users: { role: string; worker_name: string | null }[] }>('/api/users', 'users').then((u) => u.users.filter((p) => p.role === 'worker').length),
      waitingFiles(),
    ])
    customers = c
    workers = w
    waiting = sent
    workersKnown = { businessId: me.businessId, count: w }
  } catch {
    return false
  }
  if (!slot.isConnected) return false
  const ownName = me.workerName?.trim() || null
  // Sign-up day: the day the free months count from (0066; 3, 6 or 12 of them, so never worked back from their end).
  const signedUpOn = me.trialEndsOn ? me.freeFrom ?? null : null
  const done = welcomeSteps(customers, ownName, workers, !me.trialEndsOn, signedUpOn)
  // A one-person round (Liam, 19 Oct 2026) is never asked to add yourself or a worker: it is just the owner, by choice.
  const alone = worksAlone()
  if (done.customers && (done.people || alone) && done.answers && done.visit) {
    // Finished for good on this phone: a later change (a visit removed) never brings the welcome back.
    try { localStorage.setItem(hiddenKey(me.businessId), '1') } catch { /* this visit only */ }
    finishedFor = me.businessId
    lastCard = null
    slot.replaceChildren()
    return false
  }

  const link = (href: string, label: string): HTMLElement => el('a', { href, class: 'welcome-go' }, label)
  // The first booking is walked through: each part of the booking panel glows in turn (booking.ts guide).
  const book = el('button', { type: 'button', class: 'welcome-go welcome-go-button' }, 'Book your first visit')
  book.addEventListener('click', () => openBooking({ guide: true }))
  const goThrough = el('button', { type: 'button', class: 'welcome-go welcome-go-button' }, 'Go through them')
  goThrough.addEventListener('click', () => goToNoDate())
  const wait = (): HTMLElement => el('span', { class: 'welcome-wait' }, 'After your first customer')
  // Already done at sign-up, so a new owner never starts at nothing (Liam, 29 Sep): the round's name and kind, and
  // on the web the free months. Every word is what sign-up saved.
  const roundName = me.business?.name?.trim() || me.businessName?.trim() || 'Your round'
  const kindWords = TRADE_OPTIONS.find((t) => t.value === me.trade)?.label ?? ''
  const freeUntil = me.trialEndsOn && !inStoreApp() ? `free until ${shortDate(me.trialEndsOn)}` : ''
  const steps: WelcomeStep[] = [
    {
      text: 'Set up your round',
      done: true,
      doneText: [roundName, kindWords, freeUntil].filter(Boolean).join(' · '),
    },
    {
      text: 'Add your customers',
      done: done.customers,
      doneText: `${customers.length} on the round`,
      note: waiting.length ? `${SENT_WORDS} ${byWords(waiting[0])}` : undefined,
      // Many at once from a spreadsheet or another app, or one at a time.
      action: el('span', { class: 'welcome-actions' }, link('#/import', 'Import a list'), link('#/grid?add=1', 'Add one')),
    },
    ...(alone ? [] : [{
      text: 'Add yourself or a worker',
      done: done.people,
      doneText: ownName ? `You're set up to do jobs yourself, as ${ownName}` : 'A worker is set up',
      action: link('#/people', 'Open People'),
    }]),
    {
      // An answer for each customer with no date: the diary's "No date yet" line has the answers to tap.
      text: 'Go through your customers',
      done: done.answers,
      doneText: done.toAsk ? `Every customer has an answer · ${done.toAsk} to message` : 'Every customer has an answer',
      note: done.pending ? `${done.pending} still to go: a week, not sure, not interested, or they'll ask` : undefined,
      action: done.customers ? goThrough : wait(),
    },
    {
      text: 'Book your first visit',
      done: done.visit,
      doneText: 'Your first visit is in the diary',
      note: done.customers ? 'Roundly shows you how, one part at a time' : undefined,
      // Booking needs someone to book: until then the step says so instead
      // of offering a panel with nobody in it.
      action: done.customers ? book : wait(),
    },
  ]
  const count = steps.filter((s) => s.done).length
  // No visit booked yet (Liam, 19 Oct 2026, after user testing: "It needs to feel easy to book in the first customer"):
  // one big "Book your first job" (first-job.ts) in place of the list, with the steps still to do folded under it, kept
  // and counted (Astra: never lost). Once the first visit is booked, the list as before for whatever is left.
  if (!done.visit) {
    const big = el('section', { class: 'welcome-card first-job-card', 'aria-label': 'Your first job' })
    const held = firstJobWaiting()
    const go = el('button', { type: 'button', class: 'btn-primary first-job-go' }, held ? 'Your first job is waiting for signal' : 'Book your first job') as HTMLButtonElement
    go.disabled = held
    go.addEventListener('click', () => void openFirstJob())
    const mark = el('span', { class: 'first-job-mark', 'aria-hidden': 'true' }, calendarIcon())
    const own = ownName?.split(/\s+/)[0] ?? ''
    big.append(mark, el('h3', {}, own ? `Let's book your first job, ${own}` : "Let's book your first job"),
      el('p', { class: 'welcome-lede' }, "Three quick steps: who it's for, when you're going, and how much."), go)
    // A file already sent says when it lands instead of asking for one again.
    if (waiting.length) big.append(el('p', { class: 'welcome-note' }, `${SENT_WORDS} ${byWords(waiting[0])}`))
    else if (!done.customers) big.append(el('a', { href: '#/import', class: 'first-job-import' }, 'Got a list of customers already? Import them'))
    // The other steps not done yet, folded and counted (people, going through customers with no date).
    const left = steps.slice(1).filter((s) => !s.done && s.text !== 'Book your first visit' && !(s.text === 'Add your customers' && !done.customers))
    if (left.length) {
      const fold = el('details', { class: 'first-job-more' }) as HTMLDetailsElement
      fold.append(el('summary', {}, `Other things to set up (${left.length})`))
      const list = el('ol', { class: 'welcome-steps' })
      for (const s of left) {
        const li = el('li', { class: 'welcome-step' }, el('span', { class: 'welcome-words' }, el('strong', {}, s.text), s.note ? el('small', { class: 'welcome-note' }, s.note) : ''))
        if (s.action) li.append(s.action)
        list.append(li)
      }
      fold.append(list)
      big.append(fold)
    }
    lastCard = { businessId: me.businessId, card: big }
    slot.replaceChildren(big)
    return true
  }
  const card = el('section', { class: 'welcome-card', 'aria-label': 'Getting started' })
  const head = el('div', { class: 'welcome-head' })
  const name = ownName ?? me.business?.name ?? ''
  head.append(el('h3', {}, name ? `Welcome to Roundly, ${name}` : 'Welcome to Roundly'), el('span', { class: 'welcome-count' }, `${count} of ${steps.length} done`))
  card.append(head, el('p', { class: 'welcome-lede' }, 'A few steps get your round going.'))
  const list = el('ol', { class: 'welcome-steps' })
  steps.forEach((s, i) => {
    const li = el('li', { class: s.done ? 'welcome-step welcome-step-done' : 'welcome-step' })
    li.append(el('span', { class: 'welcome-num', 'aria-hidden': 'true' }, s.done ? '✓' : String(i + 1)))
    // Screen readers hear which steps are done, not only the count.
    const words = el('span', { class: 'welcome-words' }, el('strong', {}, s.done ? el('span', { class: 'sr-only' }, 'Done: ') : '', s.text))
    if (s.done && s.doneText) words.append(el('small', {}, s.doneText))
    else if (!s.done && s.note) words.append(el('small', { class: 'welcome-note' }, s.note))
    li.append(words)
    if (!s.done && s.action) li.append(s.action)
    list.append(li)
  })
  card.append(list)
  const foot = el('div', { class: 'welcome-foot' })
  foot.append(el('span', {}, 'Business name, bank details and prices are in Help, under Getting started.'))
  const hide = el('button', { type: 'button', class: 'link-button welcome-hide' }, 'Hide this')
  hide.addEventListener('click', () => {
    try {
      localStorage.setItem(hiddenKey(me.businessId), '1')
    } catch {
      // Private window: it stays hidden for this visit only.
    }
    hiddenNow.add(me.businessId)
    lastCard = null
    card.remove()
    // Focus goes to the diary's heading rather than falling to the page.
    const heading = document.querySelector<HTMLElement>('#screen .page-heading h2')
    if (heading) {
      heading.tabIndex = -1
      heading.focus()
    }
  })
  foot.append(hide)
  card.append(foot)
  lastCard = { businessId: me.businessId, card }
  slot.replaceChildren(card)
  return true
}
